/**
 * Per-user, per-browser activity signals that prompts and offers can read:
 * whether this page load is a return visit on a new day, and whether the user
 * has downloaded anything. Stored in localStorage, keyed by user id.
 */
const KEY_PREFIX = 'userActivity:v1:'

interface UserActivity {
  version: 1
  /** Local calendar day of the latest visit. */
  lastVisitDay: string | null
  hasDownloaded: boolean
}

/** Return-visit result per user for this page load, so repeated calls agree. */
const visitsThisPageLoad = new Map<string, boolean>()

export function localDayKey(now: number): string {
  const date = new Date(now)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function userActivityKey(userId: string): string {
  return `${KEY_PREFIX}${encodeURIComponent(userId)}`
}

function read(userId: string): UserActivity {
  const empty: UserActivity = { version: 1, lastVisitDay: null, hasDownloaded: false }
  try {
    const value = JSON.parse(localStorage.getItem(userActivityKey(userId)) ?? 'null') as Partial<UserActivity> | null
    if (!value || value.version !== 1 || typeof value.hasDownloaded !== 'boolean' ||
      !(value.lastVisitDay === null || (typeof value.lastVisitDay === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.lastVisitDay)))) {
      return empty
    }
    return value as UserActivity
  } catch {
    return empty
  }
}

function write(userId: string, activity: UserActivity): void {
  try {
    localStorage.setItem(userActivityKey(userId), JSON.stringify(activity))
  } catch {
    // Activity signals are best effort.
  }
}

/**
 * Records today's visit once per page load and returns whether the previous
 * visit was on a different local day. Later calls in the same page load return
 * the first result.
 */
export function recordUserVisit(userId: string, now = Date.now()): boolean {
  const known = visitsThisPageLoad.get(userId)
  if (known !== undefined) return known
  const activity = read(userId)
  const today = localDayKey(now)
  const isReturn = activity.lastVisitDay !== null && activity.lastVisitDay !== today
  write(userId, { ...activity, lastVisitDay: today })
  visitsThisPageLoad.set(userId, isReturn)
  return isReturn
}

export function recordUserDownload(userId: string): void {
  const activity = read(userId)
  if (!activity.hasDownloaded) write(userId, { ...activity, hasDownloaded: true })
}

export function hasUserDownloaded(userId: string): boolean {
  return read(userId).hasDownloaded
}

/** Test-only reset of the per-page-load cache. */
export function resetUserActivityForTests(): void {
  visitsThisPageLoad.clear()
}
